import { GuardedDeletionService } from 'projects/orchestrator/src/lib/services/guarded-deletion.service';
import { OnInit, Component, DestroyRef, inject } from '@angular/core';
import { ListService } from '@abp/ng.core';
import { STAND_CONFIG, STAND_FIELDS, WRAPPER_CONFIG } from './stand.config';
import {
  BaseHierarchicalCrudComponent,
  GenericForm,
  IActionsSchema,
  resolveActions,
  ModalBuilder,
  ZnHierarchicalViewComponent,
  ICrudService,
} from '@zendra/shared';
import { CommonModule } from '@angular/common';
import {
  StandDto,
  StandCreateDto,
  StandUpdateDto,
  StandExcelDownloadDto,
  GetStandsInputDto,
} from 'projects/cbd-proxies/src/lib/planning/dtos/stand';
import { StandService } from 'projects/cbd-proxies/src/lib/planning/controllers/stand.service';
import { StandCreationForm } from './stand-creation-form/stand-creation-form';
import { StandPlanningOrchestratorService } from 'projects/orchestrator/src/lib/proxy/orchestrator/controllers/stand-planning-orchestrator.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, tap } from 'rxjs';

@Component({
  selector: 'app-stand',
  imports: [CommonModule, GenericForm, ZnHierarchicalViewComponent],
  providers: [ListService, StandService],
  templateUrl: './stand.html',
  styleUrl: './stand.scss',
})
export class Stand extends BaseHierarchicalCrudComponent<
  StandDto,
  StandCreateDto,
  StandUpdateDto,
  StandExcelDownloadDto
> implements OnInit {
  private readonly guardedDeletion = inject(GuardedDeletionService);
  private readonly modalService = inject(ModalBuilder);

  geographicService = inject(StandService);
  orchestratorService = inject(StandPlanningOrchestratorService);
  private readonly destroyRef = inject(DestroyRef);

  service: ICrudService<StandDto, StandCreateDto, StandUpdateDto, StandExcelDownloadDto> =
    {
      create: input => this.geographicService.create(input),
      update: (id, input) =>
        this.orchestratorService.update(id, input) as Observable<StandDto>,
      deleteByIds: ids => this.guardedDeletion.deleteByIds('stands', ids),
      getList: (query: GetStandsInputDto) => this.geographicService.getList(query),
      getDownloadToken: () => this.geographicService.getDownloadToken(),
      getListAsExcelFile: input => this.geographicService.getListAsExcelFile(input),
    };

  config = STAND_CONFIG;
  fields = STAND_FIELDS;
  actions: IActionsSchema | null = null;

  ngOnInit(): void {
    if (!this.config.actions || !this.config.actionIds) {
      return;
    }

    this.actions = this.withGridRefreshOnActionCompleted(
      resolveActions(this.config.actions, {
        [this.config.actionIds.activateStandTrigger]: () => this.activateStand(),
        [this.config.actionIds.undoCreateFirstCycleRotation]: () =>
          this.undoCreateFirstCycleRotation(),
      }),
    );
  }

  protected prepareUpdateDto(data: any, selected: StandDto): StandUpdateDto {
    return {
      name: data.name,
      standArea: data.standArea,
      gisArea: data.gisArea ?? data.standArea ?? selected.standArea,
      isStrategic: data.isStrategic,
      hasNearbyPopulation: data.hasNearbyPopulation,
      currentCycle: data.currentCycle,
      currentRotation: data.currentRotation,
      isPefcCertified: data.isPefcCertified,
      isFscCertified: data.isFscCertified,
      deactivationReasonId: data.deactivationReasonId,
      deactivationDate: data.deactivationDate,
      forestrySituationId: data.forestrySituationId,
      locationId: data.locationId,
      previousSoilUseId: data.previousSoilUseId,
      propertyId: data.propertyId,
      standTypeId: data.standTypeId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'Stand';
  }

  private activateStand(): Observable<unknown> | void {
    const ids = this.selectedRows;

    if (!ids?.length) {
      return;
    }

    const standId = ids[0];

    return this.orchestratorService.activateStand(standId).pipe(
      tap({
        next: () => {
          this.toaster.success('LanguageService::Actions:SuccessMessage', '', {
            life: 5000,
            messageLocalizationParams: [
              this.localizationService.instant('Planning::Action.Trigger.ActivateStand'),
            ],
          });
        },
        error: err => {
          console.error(err);
        },
      }),
    );
  }

  private undoCreateFirstCycleRotation(): Observable<unknown> | void {
    const ids = this.selectedRows;

    if (!ids?.length) {
      return;
    }

    const standId = ids[0];

    return this.orchestratorService.undoCreateFirstCycleRotation(standId).pipe(
      tap({
        next: () => {
          this.toaster.success('LanguageService::Actions:SuccessMessage', '', {
            life: 5000,
            messageLocalizationParams: [
              this.localizationService.instant('Planning::Action.Trigger.UndoCreateFirstCycleAndRotation'),
            ],
          });
        },
        error: err => {
          console.error(err);
        },
      }),
    );
  }

  override create(): void {
    if (this.parentId !== undefined && this.parentFilterField) {
      this.selectedEntity = {
        [this.parentFilterField]: this.parentId,
      } as unknown as StandDto;
    } else {
      this.selectedEntity = {} as unknown as StandDto;
    }

    const modalRef = this.modalService.openModal(StandCreationForm, WRAPPER_CONFIG);

    modalRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refreshGrid();
    });
  }
}
